import { Path, useForm } from "react-hook-form";
import { FormText } from "../../common/forms/FormText";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import Container, { Progress } from "../../common/Container";
import Footer from "../../common/Footer";
import FooterButton from "../../common/FooterButton";
import useVerifyOrderNumber, { VerifyOrderNumberResponse } from "../../requests/useVerifyOrderNumber";
import { Order, PurchaseOrder, LoadoutGroup } from "../../UserFlow";
import SwitchToMobileQRCode from "../../common/SwitchToMobileQRCode";
import useIsAnonymous from "../../common/lib/hooks/useIsAnonymous";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import { driverFacingErrorMessage, driverFacingMessage, driverFacingResultError } from "../../common/userFacingMessages/userFacingMessages";
import { UserFacingMessageCode } from "../../common/userFacingMessages/userFacingMessageCodes";
import KaModal from "../../common/KaModal";
import { useState } from "react";
import Button from "../../common/Button";
import { FooterProps } from "./StageOrderTypes";
import StartOverButton from "../../common/StartOverButton";

interface OrderNumberProps extends FooterProps {
  siteId: string;
  progress?: Progress;
  initialOrderNumber?: string;
  orderSelected: (order: Order) => void;
  purchaseOrderSelected: (purchaseOrder: PurchaseOrder) => void;
  loadoutGroupSelected: (loadoutGroup: LoadoutGroup) => void;
  kioskId?: string;
  startOver: () => void;
}

interface OrderNumberForm {
  number: string;
}

const OrderNumber = (props: OrderNumberProps) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const displayMessage = useDisplayMessage();
  const [bothResponse, setBothResponse] = useState<VerifyOrderNumberResponse | undefined>();
  const modalError = useDisplayMessage();

  const { register, handleSubmit, setValue, formState, watch } = useForm<OrderNumberForm>({
    defaultValues: { number: props.initialOrderNumber ?? "" }
  });

  const orderNumber = watch("number");

  const formProps = (name: Path<OrderNumberForm>) => ({
    name,
    setValue,
    register,
    errors: formState.errors
  });

  const getOrder = useVerifyOrderNumber(props.siteId, orderNumber, {
    autoQuery: false,
    onSuccess: ({ loadout, purchaseOrder }) => {
      const hasLoadout = loadout.value?.loadoutGroup || loadout.value?.order || loadout.error;
      const hasPurchaseOrder = purchaseOrder.value || purchaseOrder.error;

      if (hasLoadout && hasPurchaseOrder) {
        setBothResponse({ loadout, purchaseOrder });
        return;
      }

      if (hasLoadout) {
        if (loadout.value?.loadoutGroup) {
          props.loadoutGroupSelected(loadout.value!.loadoutGroup);
        } else if (loadout.value?.order) {
          props.orderSelected(loadout.value!.order);
        } else {
          displayMessage.fail(_(driverFacingResultError(loadout.error!)));
        }

        return;
      }

      if (hasPurchaseOrder) {
        if (purchaseOrder.value) {
          props.purchaseOrderSelected(purchaseOrder.value);
        } else {
          displayMessage.fail(_(driverFacingResultError(purchaseOrder.error!)));
        }

        return;
      }

      displayMessage.fail(_(driverFacingMessage(UserFacingMessageCode.UnrecognizedOrderNumber, [])));
    },
    onError: (err) => displayMessage.fail(_(driverFacingErrorMessage(err))),
  }, props.kioskId);

  const submit = () => {
    getOrder.query();
  }

  const formWidth = isAnonymous ? "w-65" : "w-75";

  return <Container
    title={_(msg`Load Information`)}
    subtitle={_(msg`What's your order number?`)}
    progress={props.progress}>
    <KaModal
      isOpen={!!bothResponse}
      toggle={() => { setBothResponse(undefined); modalError.clear(); }}
      title={_(msg`Select Direction`)}
      className='modal-dialog-centered'
      body={(<>
        <KaAlert displayMessage={modalError.message} onClose={modalError.clear} />
        <div className="modal-body justify-content-between row">
          <Button className="col-5" onClick={() => {
            if (bothResponse!.loadout.value?.order) {
              props.orderSelected(bothResponse!.loadout.value.order);
            } else if (bothResponse!.loadout.value?.loadoutGroup) {
              props.loadoutGroupSelected(bothResponse!.loadout.value.loadoutGroup)
            } else {
              modalError.fail(_(driverFacingResultError(bothResponse!.loadout.error!)));
            }
          }}>
            <Trans>Picking up product</Trans>
          </Button>
          <Button className="col-5" onClick={() => {
            if (bothResponse!.purchaseOrder.value) {
              props.purchaseOrderSelected(bothResponse!.purchaseOrder.value);
            } else {
              modalError.fail(_(driverFacingResultError(bothResponse!.purchaseOrder.error!)));
            }
          }}>
            <Trans>Dropping off product</Trans>
          </Button>
        </div>
      </>)} />
    {isAnonymous && <>
      <StartOverButton startOver={props.startOver} />
      <SwitchToMobileQRCode />
    </>}
    <div className={formWidth + " m-auto"}>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <FormText {...formProps("number")}
        displayName={_(msg`Enter Order Number`)}
        placeholder={_(msg`Enter order number`)}
        autoComplete={!isAnonymous}
        options={{
          required: _(msg`Order Number is required`),
          validate: {
            length: v => v.length < 51 || _(msg`Order Number cannot be longer than 50 characters`)
          },
        }}
      />
    </div>
    <Footer
      backButton={<FooterButton {...props.backButton} />}
      nextButton={
        <FooterButton
          onClick={handleSubmit(submit)}
          spinning={getOrder.isLoading}
          disabled={!orderNumber}>
          <Trans>Next</Trans>
        </FooterButton>
      } />
  </Container>
}

export default OrderNumber;